/**
 * Arbre des paquets de la barre latérale (design system, ArbreDesPaquets) : un seul arrêt de
 * tabulation, flèches pour se déplacer, déplier et replier, Entrée pour ouvrir, Espace pour
 * lancer la séance du paquet principal, glisser-déposer pour ranger.
 */
import { useRef, useState, type DragEvent, type KeyboardEvent } from 'react';
import type { Compteurs } from '../../seance/compteurs';
import { aplatir, estDescendant, indexer, type NoeudPaquet } from '../donnees/arbre';
import { cx, nombre } from '../outils';
import { Icone } from './Icone';

export type PositionDepot = 'avant' | 'apres' | 'dans';

export interface ArbreDesPaquetsProps {
  noeuds: NoeudPaquet[];
  compteurs: ReadonlyMap<string, Compteurs>;
  selection: string | null;
  deplies: ReadonlySet<string>;
  onDeplier: (id: string, deplie: boolean) => void;
  onOuvrir: (id: string) => void;
  onLancer: (idPaquetPrincipal: string) => void;
  onActions: (id: string, ancre: HTMLElement | { x: number; y: number }) => void;
  onDeplacer: (id: string, cibleId: string, position: PositionDepot) => void;
  /** Paquet dont le menu est ouvert (son bouton garde aria-expanded). */
  menuOuvert?: string | null;
  idTitre: string;
}

export function nomCompteurs(c: Compteurs): string {
  return `${nombre(c.nouvelles)} ${c.nouvelles > 1 ? 'nouvelles' : 'nouvelle'}, ${nombre(c.apprentissage)} en apprentissage, ${nombre(c.aRevoir)} à revoir`;
}

export function ArbreDesPaquets({
  noeuds,
  compteurs,
  selection,
  deplies,
  onDeplier,
  onOuvrir,
  onLancer,
  onActions,
  onDeplacer,
  menuOuvert = null,
  idTitre,
}: ArbreDesPaquetsProps) {
  const index = indexer(noeuds);
  const visibles = aplatir(noeuds).filter((n) => {
    let parent = n.paquet.parentId;
    while (parent) {
      if (!deplies.has(parent)) return false;
      parent = index.get(parent)?.paquet.parentId ?? null;
    }
    return true;
  });
  const [actif, setActif] = useState<string | null>(null);
  const actifEffectif =
    visibles.find((n) => n.paquet.id === actif)?.paquet.id ??
    visibles.find((n) => n.paquet.id === selection)?.paquet.id ??
    visibles[0]?.paquet.id;
  const liste = useRef<HTMLUListElement>(null);
  const [glisse, setGlisse] = useState<string | null>(null);
  const [depot, setDepot] = useState<{ id: string; position: PositionDepot } | null>(null);

  const focaliser = (id: string) => {
    setActif(id);
    liste.current?.querySelector<HTMLElement>(`[data-id="${CSS.escape(id)}"]`)?.focus();
  };

  function clavier(e: KeyboardEvent<HTMLLIElement>, n: NoeudPaquet) {
    if (e.target !== e.currentTarget) return;
    const i = visibles.indexOf(n);
    const ouvert = deplies.has(n.paquet.id);
    switch (e.key) {
      case 'ArrowDown':
        if (visibles[i + 1]) focaliser(visibles[i + 1]!.paquet.id);
        break;
      case 'ArrowUp':
        if (visibles[i - 1]) focaliser(visibles[i - 1]!.paquet.id);
        break;
      case 'ArrowRight':
        if (n.enfants.length === 0) return;
        if (!ouvert) onDeplier(n.paquet.id, true);
        else focaliser(n.enfants[0]!.paquet.id);
        break;
      case 'ArrowLeft':
        if (ouvert && n.enfants.length > 0) onDeplier(n.paquet.id, false);
        else if (n.paquet.parentId) focaliser(n.paquet.parentId);
        break;
      case 'Home':
        if (visibles[0]) focaliser(visibles[0].paquet.id);
        break;
      case 'End':
        if (visibles.at(-1)) focaliser(visibles.at(-1)!.paquet.id);
        break;
      case 'Enter':
        onOuvrir(n.paquet.id);
        break;
      case ' ':
        onLancer(n.principalId);
        break;
      case 'F10':
        if (!e.shiftKey) return;
        onActions(n.paquet.id, e.currentTarget);
        break;
      case 'ContextMenu':
        onActions(n.paquet.id, e.currentTarget);
        break;
      default:
        return;
    }
    e.preventDefault();
  }

  function positionDepot(e: DragEvent<HTMLLIElement>): PositionDepot {
    const r = e.currentTarget.getBoundingClientRect();
    const y = (e.clientY - r.top) / r.height;
    return y < 0.25 ? 'avant' : y > 0.75 ? 'apres' : 'dans';
  }

  return (
    <ul ref={liste} className="fc-arbre-des-paquets" role="tree" aria-labelledby={idTitre}>
      {visibles.map((n) => {
        const id = n.paquet.id;
        const principal = n.niveau === 0;
        const c = compteurs.get(id);
        const idNom = `arbre-${id}-nom`;
        const idCompteurs = `arbre-${id}-c`;
        return (
          <li
            key={id}
            data-id={id}
            className="fc-arbre-des-paquets__ligne"
            role="treeitem"
            aria-level={n.niveau + 1}
            aria-expanded={n.enfants.length > 0 ? deplies.has(id) : undefined}
            aria-selected={selection === id}
            aria-labelledby={idNom}
            aria-describedby={principal && c ? idCompteurs : undefined}
            tabIndex={id === actifEffectif ? 0 : -1}
            style={{ ['--niveau' as string]: n.niveau }}
            draggable
            data-depot={depot?.id === id ? depot.position : undefined}
            onClick={() => {
              setActif(id);
              onOuvrir(id);
            }}
            onFocus={(e) => e.target === e.currentTarget && setActif(id)}
            onKeyDown={(e) => clavier(e, n)}
            onContextMenu={(e) => {
              e.preventDefault();
              onActions(id, { x: e.clientX, y: e.clientY });
            }}
            onDragStart={(e) => {
              setGlisse(id);
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData('text/plain', n.paquet.nom);
            }}
            onDragEnd={() => {
              setGlisse(null);
              setDepot(null);
            }}
            onDragOver={(e) => {
              if (glisse === null || estDescendant(index, id, glisse)) return;
              e.preventDefault();
              const position = positionDepot(e);
              if (depot?.id !== id || depot.position !== position) setDepot({ id, position });
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) setDepot(null);
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (glisse !== null && depot !== null && glisse !== id) {
                onDeplacer(glisse, id, depot.position);
              }
              setGlisse(null);
              setDepot(null);
            }}
          >
            <Icone nom="grip-vertical" className="fc-arbre-des-paquets__poignee" />
            <span
              aria-hidden="true"
              className="fc-arbre-des-paquets__chevron"
              onClick={(e) => {
                if (n.enfants.length === 0) return;
                e.stopPropagation();
                onDeplier(id, !deplies.has(id));
              }}
            >
              {n.enfants.length > 0 && <Icone nom="chevron-right" />}
            </span>
            <Icone nom={principal ? 'layers' : 'folder'} className="fc-arbre-des-paquets__icone" />
            <span className="fc-arbre-des-paquets__nom" id={idNom}>
              {n.paquet.nom}
            </span>
            <span className="fc-arbre-des-paquets__fin">
              {principal && c && (
                <span
                  className="fc-arbre-des-paquets__compteurs"
                  role="img"
                  id={idCompteurs}
                  aria-label={nomCompteurs(c)}
                >
                  {(['nouvelles', 'apprentissage', 'aRevoir'] as const).map((cle) => (
                    <span
                      key={cle}
                      className={cx(
                        'fc-arbre-des-paquets__compteur',
                        `fc-arbre-des-paquets__compteur--${cle === 'aRevoir' ? 'a-revoir' : cle}`,
                      )}
                      data-valeur={c[cle]}
                    >
                      {nombre(c[cle])}
                    </span>
                  ))}
                </span>
              )}
              <span className="fc-arbre-des-paquets__actions">
                {principal && (
                  <button
                    type="button"
                    className="fc-bouton fc-bouton--discret fc-bouton--petit fc-bouton--icone"
                    tabIndex={-1}
                    aria-label="Lancer la séance"
                    title="Lancer la séance (Espace)"
                    onClick={(e) => {
                      e.stopPropagation();
                      onLancer(id);
                    }}
                  >
                    <Icone nom="play" />
                  </button>
                )}
                <button
                  type="button"
                  className="fc-bouton fc-bouton--discret fc-bouton--petit fc-bouton--icone"
                  tabIndex={-1}
                  aria-label="Plus d’actions"
                  title="Plus d’actions"
                  aria-haspopup="menu"
                  aria-expanded={menuOuvert === id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onActions(id, e.currentTarget);
                  }}
                >
                  <Icone nom="ellipsis" />
                </button>
              </span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}
